import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS, pName, pMatch } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CHANNELS } from "./common";
import { ScanButton, scanOnEnter } from "@/components/ProductCombobox";

const StockHint = ({ s, short, i }) => {
  if (!s) return null;
  const cls = s.status === "out" || short ? "text-rose-600" : s.status === "low" ? "text-amber-600" : "text-muted-foreground";
  const msg = s.status === "out" ? "Out of stock" : short ? `Only ${s.available} in stock` : s.status === "low" ? `Low: ${s.available} left` : `${s.available} in stock`;
  return <div className={`text-[11px] mt-0.5 ${cls}`} data-testid={`ws-line-stock-${i}`}>{msg}</div>;
};

export const OrderFormDialog = ({ open, order, hospitals, onClose, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [hospitalId, setHospitalId] = useState("");
  const [channel, setChannel] = useState("phone");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [ftype, setFtype] = useState("physical");
  const [supplierId, setSupplierId] = useState("");
  const [supRef, setSupRef] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const virtual = ftype === "virtual";
  const [stock, setStock] = useState({ warehouse: "", items: {} });
  const idsKey = lines.map((l) => l.product_id).sort().join(",");
  useEffect(() => {
    if (!open || !idsKey) return;
    api.get("/wholesale/stock", { params: { product_ids: idsKey } }).then((r) => setStock(r.data)).catch(() => {});
  }, [open, idsKey]);
  const stockOf = (pid) => stock.items[pid];
  const shortOf = (l) => { const s = stockOf(l.product_id); return !virtual && s && Number(l.quantity || 0) > s.available; };

  useEffect(() => {
    if (!open) return;
    if (!products.length) api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Could not load products"));
    if (!suppliers.length) api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    setFtype(order?.fulfilment_type || "physical"); setSupplierId(order?.supplier_id || ""); setSupRef(order?.supplier_ref || "");
    setHospitalId(order?.hospital_id || ""); setChannel(order?.channel || "phone"); setRef(order?.external_ref || ""); setNote(order?.note || "");
    setLines(order ? order.lines.map((l) => ({ product_id: l.product_id, generic_name: l.generic_name, sku: l.sku, unit_cost: l.unit_cost, quantity: String(l.quantity) })) : []);
    setQ("");
  }, [open, order]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return products.filter((p) => pMatch(p, s)).slice(0, 8);
  }, [q, products]);

  const add = async (p) => {
    if (lines.some((l) => l.product_id === p.id)) return toast.info("Already on the order — adjust the quantity");
    setLines((ls) => [...ls, { product_id: p.id, generic_name: pName(p), sku: p.sku, unit_cost: p.cost_price || 0, quantity: "1" }]);
    setQ("");
    if (virtual) return;
    try {
      const { data } = await api.get("/wholesale/stock", { params: { product_ids: p.id } });
      const s = data.items[p.id];
      if (s?.status === "out") toast.error(`${pName(p)} is out of stock at ${data.warehouse}`);
      else if (s?.status === "low") toast.warning(`${pName(p)} is low at ${data.warehouse}: only ${s.available} left`);
    } catch (e) { console.warn("Stock check failed", e); }
  };
  const setQty = (i, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, quantity: v } : l)));
  const setCost = (i, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, unit_cost: v } : l)));
  const est = lines.reduce((a, l) => a + Number(l.unit_cost || 0) * Number(l.quantity || 0), 0);
  const hosp = hospitals.find((h) => h.id === hospitalId);
  const prior = order && order.hospital_id === hospitalId ? order.total_cost : 0;
  const remaining = hosp ? hosp.remaining + prior : 0;
  const over = hosp && est > remaining + 0.004;

  const save = async () => {
    const payload = { hospital_id: hospitalId, channel, external_ref: ref, note, fulfilment_type: ftype, supplier_id: supplierId || null, supplier_ref: supRef,
      lines: lines.filter((l) => Number(l.quantity) > 0).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), ...(virtual ? { unit_cost: Number(l.unit_cost || 0) } : {}) })) };
    if (!hospitalId) return toast.error("Select the Hospital Partner");
    if (virtual && !supplierId) return toast.error("Select the supplier shipping this order");
    if (over) return toast.error(`This order exceeds ${hosp.name}'s remaining allocation (${fmtGHS(remaining)}). An authorised user must raise the allocation first.`);
    if (!payload.lines.length) return toast.error("Add at least one product with a quantity");
    const short = lines.filter(shortOf);
    if (short.length) toast.warning(`Not enough stock for ${short.map((l) => l.generic_name).join(", ")} — the order will be saved as pending but can't be fulfilled until stock arrives`);
    setSaving(true);
    try {
      const { data } = order ? await api.put(`/wholesale/orders/${order.id}`, payload) : await api.post("/wholesale/orders", payload);
      toast.success(`${data.code} saved — ${fmtGHS(data.total_cost)} at cost`);
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ws-order-dialog">
        <DialogHeader>
          <DialogTitle>{order ? `Edit ${order.code}` : "New partner supply order"}</DialogTitle>
          <DialogDescription>Capture a supply order from a Hospital Partner, at cost price. It counts against the partner's approved allocation.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2" data-testid="ws-order-type">
          {[["physical", "Physical", "Dispatched from the main warehouse; stock deducted on fulfilment"], ["virtual", "Virtual", "Supplier ships straight to the hospital; no stock movement here"]].map(([v, label, hint]) => (
            <button key={v} type="button" data-testid={`ws-type-${v}`} onClick={() => setFtype(v)}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${ftype === v ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-muted/50"}`}>
              <div className="text-sm font-medium">{label}</div><div className="text-[11px] text-muted-foreground">{hint}</div>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1"><Label className="text-xs">Hospital Partner</Label>
            <Select value={hospitalId} onValueChange={setHospitalId}>
              <SelectTrigger data-testid="ws-order-hospital"><SelectValue placeholder="Select partner" /></SelectTrigger>
              <SelectContent>{hospitals.filter((h) => h.is_active !== false).map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Received via</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="ws-order-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Partner's reference (LPO no.)</Label><Input data-testid="ws-order-ref" value={ref} onChange={(e) => setRef(e.target.value)} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Supplier {virtual ? "(required)" : "(optional)"}</Label>
            <Select value={supplierId || "none"} onValueChange={(v) => setSupplierId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="ws-order-supplier"><SelectValue /></SelectTrigger>
              <SelectContent>{!virtual && <SelectItem value="none">— Not specified —</SelectItem>}{virtual && !supplierId && <SelectItem value="none" disabled>Select supplier</SelectItem>}{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Supplier ref / waybill</Label><Input data-testid="ws-order-supplier-ref" value={supRef} onChange={(e) => setSupRef(e.target.value)} /></div>
        </div>
        {hosp && (
          <div className={`text-xs rounded-md px-3 py-2 ${over ? "bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300" : "bg-muted text-muted-foreground"}`} data-testid="ws-order-credit-info">
            {over && <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />}Approved allocation {fmtGHS(hosp.allocation)} · used {fmtGHS(hosp.used)} · committed {fmtGHS(hosp.committed - prior)} · remaining {fmtGHS(remaining)}{over ? " — this order exceeds the remaining allocation; an authorised user must raise it" : ""}
          </div>
        )}
        <div className="relative">
          <Label className="text-xs">Add product</Label>
          <div className="flex gap-1">
            <Input data-testid="ws-order-product-search" placeholder="Search name, code or barcode — or scan…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={scanOnEnter(products, (p) => { add(p); setQ(""); })} />
            <ScanButton products={products} onProduct={add} testId="ws-order-scan" />
          </div>
          {matches.length > 0 && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-64 overflow-y-auto">
              {matches.map((p) => (
                <button type="button" key={p.id} data-testid={`ws-product-option-${p.sku}`} onClick={() => add(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between gap-3">
                  <span>{pName(p)}</span>
                  <span className="font-mono text-xs text-muted-foreground">{p.sku} · {fmtGHS(p.cost_price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b border-border">
            <tr><th className="py-2">Product</th><th className="py-2 w-28">Quantity</th><th className="py-2 text-right">Unit cost*</th><th className="py-2 text-right">Line total</th><th /></tr>
          </thead>
          <tbody>
            {lines.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-xs">Search above to add products</td></tr>}
            {lines.map((l, i) => (
              <tr key={l.product_id} className="border-b border-border/50">
                <td className="py-1.5">{l.generic_name} <span className="text-xs text-muted-foreground font-mono">{l.sku}</span></td>
                <td className="py-1.5"><Input type="number" min="1" className={`h-8 ${shortOf(l) ? "border-rose-500 focus-visible:ring-rose-500" : ""}`} data-testid={`ws-line-qty-${i}`} value={l.quantity} onChange={(e) => setQty(i, e.target.value)} />
                  {!virtual && <StockHint s={stockOf(l.product_id)} short={shortOf(l)} i={i} />}</td>
                <td className="py-1.5 text-right font-mono">{virtual ? <Input type="number" min="0" step="0.01" className="h-8 w-24 ml-auto text-right" data-testid={`ws-line-cost-${i}`} value={l.unit_cost} onChange={(e) => setCost(i, e.target.value)} /> : fmtGHS(l.unit_cost)}</td>
                <td className="py-1.5 text-right font-mono">{fmtGHS(Number(l.unit_cost || 0) * Number(l.quantity || 0))}</td>
                <td className="py-1.5 text-right"><Button variant="ghost" size="sm" data-testid={`ws-line-remove-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={3} className="py-2 text-right font-medium">Estimated total at cost</td><td className="py-2 text-right font-mono font-semibold" data-testid="ws-order-est-total">{fmtGHS(est)}</td><td /></tr></tfoot>
        </table>
        <p className="text-[11px] text-muted-foreground">{virtual ? "* Virtual orders: unit cost defaults to the product cost price — edit it to match the supplier's price." : "* Estimate from the current product cost. The final total is fixed from the exact warehouse batches picked (earliest expiry first) on dispatch."}</p>
        <div><Label className="text-xs">Note</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ws-order-note" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ws-order-save-btn">{saving ? "Saving…" : order ? "Save changes" : "Save order"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
